import React, { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { Modal, PageHeader, Switch } from "../../components/lawyer/LawyerUi";
import { isDarkTheme, setDarkTheme } from "../../components/lawyer/lawyerTheme";
import { apiErrorMessage, isBlankFile, userAuth, userService } from "../../services/userApi";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ar", label: "Arabic" },
];

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const Row = ({ label, to, href, onClick, right, danger, toggle }) => {
  const content = (
    <>
      <span className="lwp-settings-label">{label}</span>
      {right}
      {!danger && !toggle && <i className="bi bi-chevron-right lwp-settings-chevron" />}
    </>
  );
  const cls = `lwp-settings-row${danger ? " danger" : ""}`;
  if (to) return <Link to={to} className={cls}>{content}</Link>;
  // Rows holding a switch can't be <button>s (no nested controls); the switch is the control.
  if (toggle) return <div className={cls} onClick={onClick}>{content}</div>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls}>{content}</a>;
  return <button type="button" className={cls} onClick={onClick}>{content}</button>;
};

// "My Account" — profile summary, app links and account actions, as in the app.
const UserProfile = () => {
  const navigate = useNavigate();
  const { profile, setProfile, logout } = useOutletContext();
  const [dark, setDark] = useState(isDarkTheme());
  const [langOpen, setLangOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const basePath = process.env.REACT_APP_BASE_PATH || "";
  const isGuest = !!profile?.isGuest || profile?.name === "Guest";
  const currentLang = profile?.app_lang || "en";

  const toggleDark = () => {
    setDarkTheme(!dark);
    setDark(!dark);
  };

  const chooseLanguage = async (code) => {
    setLangOpen(false);
    if (code === currentLang) return;
    const prev = profile;
    setProfile((p) => ({ ...p, app_lang: code }));
    try {
      const res = await userService.updateProfile({ app_lang: code, language: code });
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || "Language updated");
    } catch (err) {
      setProfile(prev);
      toast.error(apiErrorMessage(err, err?.message || "Unable to update language."));
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      const res = await userService.deleteAccount();
      if (!res?.data?.status) throw new Error(res?.data?.message);
      userAuth.clear();
      toast.success(res.data.message || "Account deleted");
      navigate("/user/login", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to delete account."));
      setDeleting(false);
    }
  };

  return (
    <div className="lwp-page lwp-page-narrow">
      <Seo title="My Account | Legal Platform" noindex />
      <PageHeader title="My Account" backTo="/user/dashboard" />

      <div className="usp-account-head">
        {!isBlankFile(profile?.picture) ? (
          <img src={profile.picture} alt={profile?.name || "Profile"} className="usp-account-avatar" />
        ) : (
          <span className="usp-account-avatar initials">{initials(profile?.name)}</span>
        )}
        <div>
          <strong>{profile?.name || " "}</strong>
          <small>{isGuest ? "Guest account" : profile?.email}</small>
        </div>
      </div>

      <div className="lwp-settings usp-settings-group">
        {isGuest ? (
          <Row label="Create Account" onClick={logout} />
        ) : (
          <Row label="My Profile" to="/user/profile/edit" />
        )}
        <Row label="Contact Us" href={`${basePath}/support`} />
        <Row label="About us" href={`${basePath}/about-legal-company`} />
        <Row label="Terms & Conditions" href={`${basePath}/terms`} />
      </div>

      <div className="lwp-settings usp-settings-group">
        {/* Language switch hidden for now — restore this row to bring it back.
        <Row
          label="Language"
          onClick={() => setLangOpen(true)}
          right={<span className="lwp-settings-value">{LANGUAGES.find((l) => l.code === currentLang)?.label}</span>}
        /> */}
        <Row label="Notifications" to="/user/notifications" />
        <Row label="Dark Mode" toggle onClick={toggleDark} right={<Switch checked={dark} onChange={toggleDark} label="Dark mode" />} />
        {!isGuest && <Row label="Delete Account" danger onClick={() => setDeleteOpen(true)} />}
        <Row label={<span className="usp-danger-text">Logout</span>} danger onClick={() => setLogoutOpen(true)} />
      </div>

      <Modal open={langOpen} onClose={() => setLangOpen(false)} title="Language">
        <div className="lwp-choice-list">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              className={`lwp-choice${currentLang === l.code ? " active" : ""}`}
              onClick={() => chooseLanguage(l.code)}
            >
              <span>{l.label}</span>
              {currentLang === l.code && <i className="bi bi-check-circle-fill" />}
            </button>
          ))}
        </div>
        <p className="lwp-modal-note">Used for your notifications and emails.</p>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        title="Delete Account"
        footer={
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn lwp-btn-outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancel
            </button>
            <button type="button" className="lwp-btn lwp-btn-danger" onClick={deleteAccount} disabled={deleting}>
              {deleting ? <span className="spinner-border spinner-border-sm" /> : "Delete"}
            </button>
          </div>
        }
      >
        <p className="lwp-modal-text">
          This permanently deletes your Legal Platform account. You will be signed out and won&apos;t be able to recover your
          chats, questions or history.
        </p>
      </Modal>

      <Modal
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        title="Logout"
        footer={
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn lwp-btn-outline" onClick={() => setLogoutOpen(false)}>
              Cancel
            </button>
            <button type="button" className="lwp-btn" onClick={logout}>
              Logout
            </button>
          </div>
        }
      >
        <p className="lwp-modal-text">Are you sure you want to log out?</p>
      </Modal>
    </div>
  );
};

export default UserProfile;
